// Control-pane orchestrator (the dependency-free Pane in ../controls/pane).
// Each per-folder builder lives under ./panes/*.ts and is driven through the
// shared section registry in ./sectionBuilders.ts — the
// same registry the rebuild driver (./sections.ts) uses, so the boot build and
// the per-section rebuild never drift. `initializePane` is the top-level wirer;
// it returns a Disposer that tears down the rail controller + the pane instance.

import { Pane } from '../controls/pane/pane';

import { paneState } from './state';
import { store } from '@store/instance';
import { initControlsTabs } from './tabs';
import { buildAllSections, makeSectionCtx } from './sectionBuilders';
import { INTENTS } from '@store/intents';
import type { Disposer } from '../types';
import type { ViewerContext } from '@app/context';
import type { ParamsState } from '@store/schema';

interface InitPaneDeps {
    applySliceDisplaySettings: () => void;
    rebuildSlicesForCurrentGrid: () => void;
}

// `_params` is sourced directly from the store proxy inside the section
// registry; the positional arg is kept for the existing call signature.
export function initializePane(viewer: ViewerContext, _params: ParamsState, deps: InitPaneDeps): Disposer {
    const container = document.getElementById('controls-container');
    if (!container) {
        console.error('Controls container not found');
        return () => { /* no container */ };
    }

    // Dispose a prior pane + its rail controller before rebuilding so listeners
    // and intent subscriptions don't accumulate (HMR / re-init / embedding).
    if (paneState.teardown) {
        try { paneState.teardown(); } catch { /* swallow */ }
        paneState.teardown = null;
    }

    const offTabs = initControlsTabs({ includeSelection: !!viewer.sceneMeta.ui.showSelectionSection, viewer });

    const paneReal = new Pane({ container });
    paneState.instance = paneReal;

    const ctx = makeSectionCtx(viewer, {
        applySliceDisplaySettings: deps.applySliceDisplaySettings,
        rebuildSlicesForCurrentGrid: deps.rebuildSlicesForCurrentGrid,
    });
    buildAllSections(ctx);

    // Prime field-line display once at boot (geometry rebuild so tubes/lines
    // pick up current params). Boot-only — the rebuild path must not re-fire it.
    store.dispatch(INTENTS.SET_FIELDLINE_DISPLAY, { rebuildGeometry: true });

    // Axes / Selection-overlay visibility live in the Layers popover
    // (src/ui/layersPanel.ts) so the controls pane stays focused on parameter tuning.

    // Full teardown: rail controller + the pane instance. Stashed so a
    // rebuild disposes the prior pane, and returned so UI dispose covers it.
    paneState.teardown = (): void => {
        offTabs();
        try { paneReal.dispose(); } catch { /* swallow */ }
        paneState.teardown = null;
    };
    return paneState.teardown;
}
